import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGuidedTour } from "../../contexts/GuidedTourContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Users,
  Upload,
  ArrowRight,
  ArrowLeft,
  LogOut,
  Rocket,
  Scale,
  Briefcase,
  CheckCircle,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import Logo from "../../assets/KavoaLogo.png";
import { firmApi, usersApi } from "../../services/api";
import { errorMessage, notifyError } from "../ui/Toast";
import WizardStepIndicator from "../intake/wizard/WizardStepIndicator";
import FirmProfileStep from "./steps/FirmProfileStep";
import InviteTeamStep, { type TeamInvite, ROLE_DEFAULT_PERMISSIONS } from "./steps/InviteTeamStep";
import AddClientsStep from "./steps/AddClientsStep";
import ImportDataStep from "./steps/ImportDataStep";
import PracticeAreaStep from "./steps/PracticeAreaStep";
import FirmTypeStep from "./steps/FirmTypeStep";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface OnboardingWizardProps {
  onComplete: () => void;
  onSignOut: () => void;
}

interface FirmData {
  name: string;
  primaryEmail: string;
  primaryPhone: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    region: string;
    postal: string;
    country: string;
  };
  jurisdiction: {
    country: string;
    region: string;
  };
  branding: {
    logoUrl: string | null;
    primaryColor: string;
    accentColor: string;
  };
  timezone: string;
  currency: string;
  numbering: {
    matterPrefix: string;
    invoicePrefix: string;
    receiptPrefix: string;
  };
  practiceAreas: string[];
  firmType: "solicitor" | "litigation" | "full_service";
}

interface BillingSetup {
  stripeConnected: boolean;
  defaultRateTableId: string | null;
}

// ─── Step Definitions ───────────────────────────────────────────────────────────

const STEPS = [
  { label: "Firm Profile", icon: Building2, key: "firmProfile" },
  { label: "Practice Areas", icon: Briefcase, key: "practiceAreas" },
  { label: "Firm Type", icon: Scale, key: "firmType" },
  { label: "Team", icon: Users, key: "inviteTeam", optional: true },
  { label: "Clients", icon: Users, key: "addClients", optional: true },
  { label: "Import", icon: Upload, key: "importData", optional: true },
  { label: "Launch", icon: Rocket, key: "launch" },
];

// Steps that can be skipped (indices)
const SKIPPABLE_STEPS = new Set([3, 4, 5]);

// ─── Default State ──────────────────────────────────────────────────────────────

const DEFAULT_FIRM_DATA: FirmData = {
  name: "",
  primaryEmail: "",
  primaryPhone: "",
  address: {
    line1: "",
    line2: "",
    city: "",
    region: "",
    postal: "",
    country: "",
  },
  jurisdiction: {
    country: "",
    region: "",
  },
  branding: {
    logoUrl: null,
    primaryColor: "#0F5132",
    accentColor: "#198754",
  },
  timezone: "",
  currency: "USD",
  numbering: {
    matterPrefix: "MAT-",
    invoicePrefix: "INV-",
    receiptPrefix: "REC-",
  },
  practiceAreas: [],
  firmType: "full_service",
};

// ─── Animation Variants ─────────────────────────────────────────────────────────

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
  }),
};

// ─── Component ──────────────────────────────────────────────────────────────────

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete, onSignOut }) => {
  const navigate = useNavigate();
  const { startTour } = useGuidedTour();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [firmData, setFirmData] = useState<FirmData>(DEFAULT_FIRM_DATA);
  const [teamInvites, setTeamInvites] = useState<TeamInvite[]>([]);
  const [billingSetup, setBillingSetup] = useState<BillingSetup>({
    stripeConnected: false,
    defaultRateTableId: null,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [importDone, setImportDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stepsCompleted, setStepsCompleted] = useState<Record<string, boolean>>({});
  const [validationError, setValidationError] = useState("");

  // Track whether we're programmatically changing the step (vs browser back/forward)
  const isProgrammaticNav = useRef(false);

  // Auto-save handler registered by the Clients step
  const clientsSaveRef = useRef<(() => Promise<void>) | null>(null);

  // ── Restore step from localStorage (e.g. returning from Stripe) ───────────

  useEffect(() => {
    const savedStep = localStorage.getItem("kavoa_onboarding_step");
    if (savedStep !== null) {
      const step = parseInt(savedStep, 10);
      if (step >= 0 && step < STEPS.length) {
        setCurrentStep(step);
      }
      localStorage.removeItem("kavoa_onboarding_step");
    }
  }, []);

  // ── Sync step changes to browser history ──────────────────────────────────

  useEffect(() => {
    if (isProgrammaticNav.current) {
      // This step change came from our navigation handlers  -  push to history
      window.history.pushState({ onboardingStep: currentStep }, "");
      isProgrammaticNav.current = false;
    }
  }, [currentStep]);

  // ── Handle browser back/forward ───────────────────────────────────────────

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (e.state && typeof e.state.onboardingStep === "number") {
        const step = e.state.onboardingStep;
        setDirection(step < currentStep ? -1 : 1);
        setCurrentStep(step);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentStep]);

  // ── Fetch initial data on mount ─────────────────────────────────────────────

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [profileRes, statusRes] = await Promise.all([
          firmApi.getProfile(),
          firmApi.getOnboardingStatus(),
        ]);

        if (profileRes.data) {
          const firm = profileRes.data;
          // Ensure no undefined values reach controlled inputs
          const safeAddr = firm.address || {};
          const safeJuris = firm.jurisdiction || {};
          const safeBranding = firm.branding || {};
          const safeNumbering = firm.settings?.numbering || {};

          setFirmData((prev) => ({
            ...prev,
            name: firm.name || prev.name,
            primaryEmail: firm.primaryEmail || prev.primaryEmail,
            primaryPhone: firm.primaryPhone || prev.primaryPhone,
            address: {
              line1: safeAddr.line1 ?? prev.address.line1,
              line2: safeAddr.line2 ?? prev.address.line2,
              city: safeAddr.city ?? prev.address.city,
              region: safeAddr.region ?? prev.address.region,
              postal: safeAddr.postal ?? prev.address.postal,
              country: safeAddr.country ?? prev.address.country,
            },
            jurisdiction: {
              country: safeJuris.country ?? prev.jurisdiction.country,
              region: safeJuris.region ?? prev.jurisdiction.region,
            },
            branding: {
              logoUrl: safeBranding.logoUrl ?? prev.branding.logoUrl,
              primaryColor: safeBranding.primaryColor ?? prev.branding.primaryColor,
              accentColor: safeBranding.accentColor ?? prev.branding.accentColor,
            },
            timezone: firm.settings?.timezone || prev.timezone,
            currency: firm.settings?.currency || prev.currency,
            numbering: {
              matterPrefix: safeNumbering.matterPrefix ?? prev.numbering.matterPrefix,
              invoicePrefix: safeNumbering.invoicePrefix ?? prev.numbering.invoicePrefix,
              receiptPrefix: safeNumbering.receiptPrefix ?? prev.numbering.receiptPrefix,
            },
            practiceAreas: firm.practiceAreas?.length ? firm.practiceAreas : prev.practiceAreas,
            firmType: firm.firmType || prev.firmType,
          }));
        }

        if (statusRes.data?.stepsCompleted) {
          setStepsCompleted(statusRes.data.stepsCompleted);
        }
      } catch (err) {
        console.error("Failed to fetch onboarding data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // ── Step Save Functions ─────────────────────────────────────────────────────

  const saveFirmProfile = useCallback(async () => {
    // Only send non-empty values to avoid Zod validation failures
    const payload: Record<string, any> = {};
    if (firmData.name) payload.name = firmData.name;
    if (firmData.primaryEmail) payload.primaryEmail = firmData.primaryEmail;
    if (firmData.primaryPhone) payload.primaryPhone = firmData.primaryPhone;

    // Only include address if at least one field is filled
    const addr = firmData.address;
    if (addr.line1 || addr.city || addr.region || addr.postal || addr.country) {
      payload.address = addr;
    }

    if (firmData.jurisdiction.country || firmData.jurisdiction.region) {
      payload.jurisdiction = firmData.jurisdiction;
    }

    // Send branding  -  never send blob: URLs to the server
    const logoUrl = firmData.branding.logoUrl;
    payload.branding = {
      primaryColor: firmData.branding.primaryColor,
      accentColor: firmData.branding.accentColor,
      ...(logoUrl && !logoUrl.startsWith("blob:") ? { logoUrl } : {}),
    };

    // Practice areas & firm type
    if (firmData.practiceAreas.length > 0) {
      payload.practiceAreas = firmData.practiceAreas;
    }
    if (firmData.firmType) {
      payload.firmType = firmData.firmType;
    }

    const settings: Record<string, any> = {};
    if (firmData.timezone) settings.timezone = firmData.timezone;
    if (firmData.currency) settings.currency = firmData.currency;
    if (firmData.numbering) settings.numbering = firmData.numbering;
    if (Object.keys(settings).length) payload.settings = settings;

    await firmApi.updateProfile(payload);

    // Upload logo if one was selected
    if (logoFile) {
      try {
        const formData = new FormData();
        formData.append("logo", logoFile);
        await firmApi.uploadLogo(formData);
      } catch (err) {
        console.error("Failed to upload logo:", err);
        // Don't block onboarding for logo failure
      }
    }
  }, [firmData, logoFile]);

  const saveInviteTeam = useCallback(async () => {
    // Only send invites that have both a valid email and a name
    const validInvites = teamInvites.filter(
      (invite) => invite.email.trim() !== "" && invite.name.trim() !== ""
    );

    if (validInvites.length === 0) return;

    // Use allSettled so one failure doesn't block the rest
    const results = await Promise.allSettled(
      validInvites.map((invite) =>
        usersApi.invite({
          email: invite.email.trim(),
          name: invite.name.trim(),
          role: invite.role,
          permissions: invite.permissions,
        })
      )
    );

    // Check if any truly failed (not just resent)
    const failedEmails = results
      .map((r, i) => (r.status === "rejected" ? validInvites[i].email.trim() : null))
      .filter((e): e is string => !!e);
    if (failedEmails.length > 0) {
      notifyError(
        `Couldn't send ${failedEmails.length === 1 ? "an invite" : `${failedEmails.length} invites`} to ${failedEmails.join(", ")}. You can invite them later from Settings.`
      );
    }
  }, [teamInvites]);

  const savePracticeAreas = useCallback(async () => {
    await firmApi.updateProfile({ practiceAreas: firmData.practiceAreas });
  }, [firmData.practiceAreas]);

  const saveFirmType = useCallback(async () => {
    await firmApi.updateProfile({ firmType: firmData.firmType });
  }, [firmData.firmType]);

  const stepSaveFunctions: Record<number, (() => Promise<void>) | null> = {
    0: saveFirmProfile,
    1: savePracticeAreas,
    2: saveFirmType,
    3: saveInviteTeam,
    4: async () => {
      // Save any filled rows the user didn't save themselves
      if (clientsSaveRef.current) await clientsSaveRef.current();
    },
    5: null, // Import handled inline
    6: null, // Launch step  -  no save needed
  };

  // ── Step Validation ───────────────────────────────────────────────────────

  const validateCurrentStep = useCallback((): string | null => {
    switch (currentStep) {
      case 0: // Firm Profile
        if (!firmData.name.trim()) return "Please enter your firm name to continue.";
        return null;
      case 1: // Practice Areas
        if (firmData.practiceAreas.length === 0) return "Please select at least one practice area to continue.";
        return null;
      default:
        return null;
    }
  }, [currentStep, firmData.name, firmData.practiceAreas]);

  // ── Navigation Handlers ─────────────────────────────────────────────────────

  const handleNext = useCallback(async () => {
    // Validate before proceeding
    const error = validateCurrentStep();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError("");

    setLoading(true);
    try {
      // Run save function for current step if one exists
      const saveFn = stepSaveFunctions[currentStep];
      if (saveFn) {
        await saveFn();
      }

      // Mark step as complete
      const stepKey = STEPS[currentStep].key;
      await firmApi.completeOnboardingStep(stepKey);
      setStepsCompleted((prev) => ({ ...prev, [stepKey]: true }));

      // Advance to next step
      if (currentStep < STEPS.length - 1) {
        isProgrammaticNav.current = true;
        setDirection(1);
        setCurrentStep((prev) => prev + 1);
      }
    } catch (err: any) {
      console.error("Failed to save step:", err);
      const fallback = "Couldn't save this step. Please try again.";
      setValidationError(
        !err?.response && err?.message && err.message !== "Network Error"
          ? err.message
          : errorMessage(err, fallback)
      );
    } finally {
      setLoading(false);
    }
  }, [currentStep, stepSaveFunctions, validateCurrentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setValidationError("");
      isProgrammaticNav.current = true;
      setDirection(-1);
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleSkipStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setValidationError("");
      isProgrammaticNav.current = true;
      setDirection(1);
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const handleComplete = useCallback(async () => {
    setValidationError("");
    setLoading(true);
    try {
      await firmApi.completeOnboarding();
      onComplete();
      // Start the guided tour now that onboarding is done  -  this is the
      // intentional trigger so the tour begins exactly when the user clicks
      // "Launch Kavoa", not on login or any other implicit event.
      startTour();
      navigate("/");
    } catch (err) {
      console.error("Failed to complete onboarding:", err);
      setValidationError(errorMessage(err, "Couldn't finish setting up your workspace. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [onComplete, navigate, startTour]);

  // ── Step Content Renderer ───────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <FirmProfileStep
            data={firmData}
            onChange={setFirmData}
            onLogoFile={(file) => setLogoFile(file)}
          />
        );
      case 1:
        return (
          <PracticeAreaStep
            selected={firmData.practiceAreas}
            onChange={(areas) =>
              setFirmData((prev) => ({ ...prev, practiceAreas: areas }))
            }
          />
        );
      case 2:
        return (
          <FirmTypeStep
            selected={firmData.firmType}
            onChange={(type) =>
              setFirmData((prev) => ({ ...prev, firmType: type }))
            }
          />
        );
      case 3:
        return (
          <InviteTeamStep
            invites={teamInvites}
            onChange={setTeamInvites}
            currentUserEmail={firmData.primaryEmail}
            onSkip={handleSkipStep}
          />
        );
      case 4:
        return (
          <AddClientsStep
            onSkip={handleSkipStep}
            saveRef={clientsSaveRef}
          />
        );
      case 5:
        return (
          <ImportDataStep
            importDone={importDone}
            onImportComplete={() => setImportDone(true)}
            onSkip={handleSkipStep}
          />
        );
      case 6:
        return (
          <div className="flex flex-col items-center text-center py-8">
            {/* Animated rocket icon */}
            <motion.div
              className="relative mb-8"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="h-24 w-24 rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                <motion.div
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Rocket className="h-12 w-12 text-brand" />
                </motion.div>
              </div>
              {/* Sparkle accents */}
              <motion.div
                className="absolute -top-1 -right-1"
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, duration: 0.4, type: "spring" }}
              >
                <Sparkles className="h-6 w-6 text-amber-400" />
              </motion.div>
              <motion.div
                className="absolute -bottom-1 -left-2"
                initial={{ scale: 0, rotate: 20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.5, duration: 0.4, type: "spring" }}
              >
                <Sparkles className="h-5 w-5 text-emerald-400" />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                You're all set!
              </h2>
              <p className="text-slate-500 text-sm max-w-sm mx-auto mb-2">
                Your workspace is configured and ready to go. Welcome to Kavoa.
              </p>
            </motion.div>

            {/* Completed steps summary */}
            <motion.div
              className="w-full max-w-xs mx-auto mt-6 space-y-2"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              {STEPS.slice(0, -1).map((step) => {
                const StepIcon = step.icon;
                const completed = stepsCompleted[step.key];
                // Hide uncompleted optional steps
                if (!completed && step.optional) return null;
                return (
                  <div
                    key={step.key}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm ${
                      completed
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-50 text-slate-400"
                    }`}
                  >
                    {completed ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <StepIcon className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className={completed ? "font-medium" : ""}>
                      {step.label}
                    </span>
                    {completed && step.optional && (
                      <span className="ml-auto text-xs text-emerald-500/60 font-medium">Optional</span>
                    )}
                    {completed && !step.optional && (
                      <span className="ml-auto text-xs text-emerald-500 font-medium">Done</span>
                    )}
                  </div>
                );
              })}
            </motion.div>

            {/* Launch button */}
            <motion.button
              onClick={handleComplete}
              disabled={loading}
              className="mt-8 flex items-center gap-3 px-10 py-4 rounded-2xl font-bold text-white text-lg bg-brand hover:bg-brand-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-200/50"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Launch Kavoa
              <Rocket className="h-5 w-5" />
            </motion.button>
          </div>
        );
      default:
        return null;
    }
  };

  // ── Determine button states ─────────────────────────────────────────────────

  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="min-h-screen flex flex-col">
      {/* ── Top Bar ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100">
        <div className="flex items-center">
          <img src={Logo} alt="Kavoa" className="h-10" />
        </div>
        <button
          onClick={onSignOut}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-600 transition-colors"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>

      {/* ── Main Content ─────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center px-6 py-10">
        {/* Step Indicator */}
        <div className="w-full max-w-xl mb-10">
          <WizardStepIndicator
            currentStep={currentStep}
            steps={STEPS.map((s) => ({ label: s.label, icon: s.icon, optional: s.optional }))}
          />
        </div>

        {/* Step Content */}
        <div className="w-full max-w-2xl flex-1">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Validation error */}
        {validationError && (
          <div className="w-full max-w-2xl mt-4">
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{validationError}</p>
            </div>
          </div>
        )}

        {/* ── Footer ───────────────────────────────────────────────────────────── */}
        <div className="w-full max-w-2xl mt-4 flex items-center justify-between">
          {/* Back Button */}
          <div>
            {!isFirstStep && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-slate-600 border border-slate-300 hover:bg-slate-50 transition-all"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
          </div>

          {/* Spacer */}
          <div />

          {/* Next Button (hidden on launch step) */}
          <div>
            {!isLastStep && (
              <button
                onClick={handleNext}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-brand hover:bg-brand-hover text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next Step
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
